import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { setUserOptions } from '../redux/builderStore';
import { useAppDispatch, useAppSelector, RootState } from '../redux/builderStoreHooks';
import { generateUserKey } from '../utils/storage';

import '@esri/calcite-components/dist/components/calcite-button';
import '@esri/calcite-components/dist/components/calcite-dropdown';
import '@esri/calcite-components/dist/components/calcite-dropdown-group';
import '@esri/calcite-components/dist/components/calcite-dropdown-item';
import {
    CalciteButton,
    CalciteDropdown,
    CalciteDropdownGroup,
    CalciteDropdownItem,
    CalciteTooltip
} from '@esri/calcite-components-react';

export const DEFAULT_APP_OPTIONS: { [id: string]: string } = {
    id: 'application',
    fontSize: 'standard',
    uitheme: 'calcite-mode-light'
};

const DEFAULT_OPTIONS = [DEFAULT_APP_OPTIONS];

const UserOptionsSubMenu = () => {
    const intl = useIntl(),
        userOptions: any[] = useAppSelector((state: RootState) => state.userOptions) ?? {},
        user: any = useAppSelector((state: RootState) => state.appSettings.user) ?? {},
        userKey = generateUserKey(user),
        dispatch = useAppDispatch(),
        appOptions =
            userOptions.find((o: any) => o.id === 'application' && o.user === userKey) ??
            DEFAULT_APP_OPTIONS;

    const changeUserOption = (evt: any) => {
        const box = evt.currentTarget,
            key = box.getAttribute('data-option-key'),
            levelKey = box.getAttribute('data-option-level'),
            v = box.getAttribute('data-option-value') ?? box.value,
            nv = box.getAttribute('data-option-value-reversed') || `${v}-reversed`,
            isChecked = (box.type === 'checkbox' && box.checked) || box.type !== 'checkbox';
        setUserOption(key, v, nv, isChecked, levelKey ?? 'application');
    };

    const setUserOption = (
        key: string,
        v: any,
        nv: any,
        isChecked: boolean,
        optionSubsetKey = 'application'
    ) => {
        if (userKey === '') {
            return;
        }
        const hasOptions =
                userOptions !== undefined
                    ? userOptions.findIndex(
                          (o: any) => o.id === optionSubsetKey && o.user === userKey
                      )
                    : -1,
            defaultOptions = DEFAULT_OPTIONS.find((o) => o.id === optionSubsetKey) ?? {},
            activeOptions =
                hasOptions >= 0
                    ? {
                          ...defaultOptions,
                          ...userOptions.find(
                              (o: any) => o.id === optionSubsetKey && o.user === userKey
                          ),
                          user: userKey
                      }
                    : { ...defaultOptions, user: userKey };
        if (key !== undefined && key !== null) {
            const [key1, key2] = key.split('.'),
                cloneOptions = [...userOptions];
            if (activeOptions[key1] !== undefined) {
                if (v !== undefined && v !== null && key2 !== undefined) {
                    activeOptions[key1][key2] = isChecked ? v : nv;
                } else if (key2 !== undefined) {
                    activeOptions[key1][key2] =
                        activeOptions[key1][key2] !== undefined &&
                        activeOptions[key1][key2] === true
                            ? false
                            : true;
                } else if (v !== undefined && v !== null) {
                    activeOptions[key1] = isChecked ? v : nv;
                }
                if (hasOptions < 0) {
                    cloneOptions.push(activeOptions);
                } else {
                    cloneOptions.splice(hasOptions, 1, activeOptions);
                }
            }
            dispatch(setUserOptions(cloneOptions));
        }
    };

    if (user === undefined || user === null || !user?.username) {
        return null;
    }

    return (
        <>
            <CalciteDropdown>
                <CalciteButton
                    id="rbOptionsButton_NavBar"
                    slot="trigger"
                    kind="neutral"
                    appearance="transparent"
                    iconStart="handle-vertical"
                    label={intl.formatMessage({
                        id: 'app.button.options',
                        defaultMessage: 'Options'
                    })}
                ></CalciteButton>
                <CalciteDropdownGroup selectionMode="multiple" style={{ minWidth: '14em' }}>
                    <CalciteDropdownItem
                        onCalciteDropdownItemSelect={changeUserOption}
                        selected={
                            appOptions.fontSize !== undefined && appOptions.fontSize === 'small'
                                ? true
                                : undefined
                        }
                        id="fontSizeSmallBox"
                        key="fontSizeSmallBox"
                        data-option-key="fontSize"
                        data-option-level="application"
                        data-option-value-reversed="standard"
                        data-option-value={
                            appOptions.fontSize !== undefined && appOptions.fontSize === 'small'
                                ? 'standard'
                                : 'small'
                        }
                    >
                        <FormattedMessage
                            id="app.button.toggleAppFontSize"
                            defaultMessage="Small text size"
                        />
                    </CalciteDropdownItem>
                </CalciteDropdownGroup>
            </CalciteDropdown>
            <CalciteTooltip
                referenceElement="rbOptionsButton_NavBar"
                overlayPositioning="fixed"
                placement="trailing"
                closeOnClick={true}
            >
                {intl.formatMessage({
                    id: 'app.button.options',
                    defaultMessage: 'Options'
                })}
            </CalciteTooltip>
        </>
    );
};

export default UserOptionsSubMenu;
